/**
 * archetypeEffectBuilder — graduates the `vfxArchetypes.config` prototype from
 * a suggestion shape into REAL `VFXEffectConfig` objects the VFXEffectRegistry
 * can register and VFXEffectPlayer can render. This is the wiring step the
 * design doc (`docs/architecture/ABILITY_VFX_ARCHETYPES.md`) describes as the
 * "mechanical spread this into the real primitive" graduation.
 *
 * Called by `namedEffectGenerator.registerNamedAbilityEffects` when a TOML
 * cast/impact name has no bespoke effect and no ABILITY_VFX_MAP entry: instead
 * of the generic school-derived burst (`buildSchoolEffect`), the ability gets
 * an archetype-derived effect that carries its family's PALETTE + MOTION
 * SIGNATURE (frost = hard shockwave + shard scatter, fire = soft bloom + embers,
 * arcane = sharp star + sparkle, etc.). The result: a new Mage frost ability
 * with no bespoke VFX still reads as part of the frost kit, not a random
 * recolored circle.
 *
 * Authority model unchanged: TOML names still win when they resolve to a
 * registered effect; the ABILITY_VFX_MAP fallback still wins when it has a
 * registered target. The archetype only fires for the deepest fallback tier
 * (the "unbuilt" bucket — no bespoke, no map entry), giving that tier a
 * family-coherent look instead of a generic school preset.
 */
import type { VFXEffectConfig, VFXPrimitive } from '../VFXEffectRegistry';
import type { AbilityVfxArchetype, ArchetypePrimitiveKind } from '../../../config/abilities/vfxArchetypes.config';
import { SCHOOL_PARTICLE_BURST } from './ParticleEffects';

/** Map the archetype's primitive-kind enum to the real VFXPrimitive `type`. */
const PRIMITIVE_TYPE_MAP: Record<ArchetypePrimitiveKind, VFXPrimitive['type']> = {
  circle: 'circle',
  star: 'star',
  shockwave: 'shockwave',
  cone: 'cone',
  rect: 'rect',
  arc: 'arc',
};

/** Build the impact-burst primitives from an archetype's motion + palette.
 *  Mirrors the shape patterns already in MageVFX.ts (shockwave for frost,
 *  circle-bloom for fire, star for arcane) but driven by config, not hardcoded. */
function buildImpactPrimitives(archetype: AbilityVfxArchetype): VFXPrimitive[] {
  const { palette, motion } = archetype;
  const primitives: VFXPrimitive[] = [];

  // School particle layer (2026-07-16 expansion): archetype impacts were
  // shapes-only — arch_impact_* now carries the school's particle family on
  // top of the shape identity, same as bespoke school hits.
  const particleId = SCHOOL_PARTICLE_BURST[archetype.school];
  if (particleId) {
    primitives.push({ type: 'particles', particleEffectId: particleId, particleCount: 7 } as VFXPrimitive);
  }

  // Primary impact shape (shockwave/circle/star/cone) — the family's shape identity.
  const primaryType = PRIMITIVE_TYPE_MAP[motion.impactPrimitive];
  const primary: VFXPrimitive = {
    type: primaryType,
    color: palette.primary,
    alpha: 0.7,
    depth: 94,
    glow: motion.glow,
    tween: {
      alpha: 0,
      scale: 2,
      duration: motion.impactDurationMs,
      ease: motion.impactEase,
    },
  };
  if (primaryType === 'circle') primary.radius = motion.impactRadius;
  if (primaryType === 'star') {
    primary.points = 5;
    primary.innerRadius = Math.round(motion.impactRadius * 0.4);
    primary.outerRadius = motion.impactRadius;
  }
  if (primaryType === 'shockwave') {
    primary.shockwaveInnerRadius = Math.round(motion.impactRadius * 0.5);
    primary.shockwaveOuterRadius = motion.impactRadius * 2;
  }
  if (primaryType === 'cone') {
    primary.coneRadius = motion.impactRadius * 2;
    primary.coneStartAngle = -45;
    primary.coneEndAngle = 45;
  }
  if (primaryType === 'arc') {
    primary.arcRadius = motion.impactRadius;
    primary.arcStartAngle = -60;
    primary.arcEndAngle = 60;
  }
  if (primaryType === 'rect') {
    primary.width = motion.impactRadius;
    primary.height = 4;
  }
  primitives.push(primary);

  // Accent layer — a smaller, brighter inner burst in the first accent color.
  if (palette.accents.length > 0) {
    primitives.push({
      type: 'circle',
      radius: Math.round(motion.impactRadius * 0.6),
      color: palette.accents[0],
      alpha: 0.9,
      depth: 95,
      glow: motion.glow,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: Math.round(motion.impactDurationMs * 0.7),
        ease: motion.impactEase,
      },
    });
  }

  // Scatter debris (shards/embers/sparkles) — the family's secondary shape.
  if (motion.scatter) {
    const scatterType = PRIMITIVE_TYPE_MAP[motion.scatter.primitive];
    const scatter: VFXPrimitive = {
      type: scatterType,
      color: palette.accents[palette.accents.length - 1] ?? palette.primary,
      alpha: 0.8,
      depth: 96,
      glow: motion.glow,
      scatter: { count: motion.scatter.count, radius: motion.scatter.radius },
      tween: {
        alpha: 0,
        scale: 0.3,
        duration: motion.impactDurationMs + 100,
        ease: motion.impactEase,
      },
    };
    if (scatterType === 'rect') {
      scatter.width = 6;
      scatter.height = 6;
    }
    if (scatterType === 'star') {
      scatter.points = 4;
      scatter.innerRadius = 2;
      scatter.outerRadius = 5;
    }
    if (scatterType === 'circle') {
      scatter.radius = 4;
    }
    primitives.push(scatter);
  }

  return primitives;
}

/** Build the cast-side charge/glow primitives from an archetype's cast motif. */
function buildCastPrimitives(archetype: AbilityVfxArchetype): VFXPrimitive[] {
  const { palette, motion } = archetype;
  if (!motion.castPrimitive) return [];

  const castType = PRIMITIVE_TYPE_MAP[motion.castPrimitive];
  const cast: VFXPrimitive = {
    type: castType,
    color: palette.glow,
    alpha: 0.5,
    depth: 90,
    glow: true,
    tween: {
      alpha: 0.8,
      scale: 0.6,
      duration: motion.castDurationMs ?? 250,
      ease: 'Power2',
      yoyo: true,
      repeat: 1,
    },
  };
  if (castType === 'circle') cast.radius = 20;
  if (castType === 'star') {
    cast.points = 5;
    cast.innerRadius = 4;
    cast.outerRadius = 10;
  }

  return [cast];
}

/** Build a complete VFXEffectConfig from an archetype for the given field
 *  (cast or impact). The effect carries the family's palette + motion
 *  signature, registered under the TOML-declared name so it plays when
 *  `vfx:spawn` fires with that name. */
export function buildArchetypeEffectConfig(
  name: string,
  field: 'cast' | 'impact',
  archetype: AbilityVfxArchetype,
  /** The ability's own authored color (F5-015) — replaces the family primary +
   *  glow so authored intent survives, while the motion signature and accent
   *  layers keep the family look. */
  authoredColor?: number,
): VFXEffectConfig {
  const effective = authoredColor != null
    ? { ...archetype, palette: { ...archetype.palette, primary: authoredColor, glow: authoredColor } }
    : archetype;
  const primitives = field === 'impact'
    ? buildImpactPrimitives(effective)
    : buildCastPrimitives(effective);

  return {
    id: name,
    name: `${name} (archetype: ${archetype.id} ${field})`,
    category: 'burst',
    description: `Auto-generated from ${archetype.id} archetype — ${field} effect with family palette + motion signature.`,
    primitives,
  };
}
